import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Settings, Plus, X, ChevronUp, ChevronDown,
  CheckCircle2, AlertCircle, Package, Gem, ArrowRight, Search,
  ClipboardList, Factory, Lock, User, Clock, CheckSquare,
  Image as ImageIcon, Trash2, Menu
} from 'lucide-react';
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip
} from 'recharts';
import SkeletonLoader from './components/SkeletonLoader';
import ApiButton from './components/ApiButton';
import { BREAKPOINTS } from './productionTheme';
import { getAuthToken, logout, refreshAccessToken } from '../api';

// ==========================================
// API CONFIGURATION (Integrated directly)
// ==========================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5011/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  async (config) => {
    let token = getAuthToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const remainingMs = (Number(payload?.exp || 0) * 1000) - Date.now();
        if (remainingMs <= 60 * 1000) {
          token = await refreshAccessToken();
        }
      } catch {
        // Keep the current token and let the backend validate it.
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      try {
        const nextToken = await refreshAccessToken();
        const retryConfig = { ...(error.config || {}) };
        retryConfig.headers = retryConfig.headers || {};
        retryConfig.headers.Authorization = `Bearer ${nextToken}`;
        return api(retryConfig);
      } catch {
        await logout({ remote: true });
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Production API methods
const productionApi = {
  // Design Master endpoints
  getDesigns: (params) => api.get('/job-cards/designs', { params }),
  getDesignById: (id) => api.get(`/master-design/designs/${id}`),
  getActiveDesigns: () => api.get('/master-design/designs?status=ACTIVE'),

  // Job Card endpoints
  getJobCards: (params) => api.get('/job-cards', { params }),
  getJobCardById: (id) => api.get(`/job-cards/${id}`),
  getJobCardStages: (id) => api.get(`/job-cards/${id}/stages`),
  getJobCardCostEstimate: (id) => api.get(`/job-cards/${id}/cost-estimate`),
  getJobCardInventoryReconciliation: (id) => api.get(`/job-cards/${id}/inventory-reconciliation`),
  createJobCard: (data) => api.post('/job-cards', data),
  createBatchJobCards: (data) => api.post('/job-cards/batch-create', data),
  addJobCardStage: (id, data) => api.post(`/job-cards/${id}/stages`, data),
  deleteJobCardStage: (stageId) => api.delete(`/job-cards/stages/${stageId}`),
  getJobCardsByStage: (stageCode) => api.get(`/job-cards/by-stage`, { params: { stage_code: stageCode } }),
  getJobCardsWithStage: (stageCode) => api.get(`/job-cards/by-stage/stages`, { params: { stage_code: stageCode } }),
  getLookups: () => api.get('/master-design/lookups'),

  // Stage Management
  updateStageStatus: (stageId, status) => api.patch(`/job-cards/stages/${stageId}/status`, { status }),

  // Workers
  getWorkers: (params) => api.get('/job-cards/workers', { params }),

  // Materials
  getMaterialsWithStock: () => api.get('/job-cards/materials-with-stock'),
  getDesignStageMaterials: (designCode, stageCode) =>
    api.get(`/job-cards/designs/code/${encodeURIComponent(designCode)}/stages/${encodeURIComponent(stageCode)}/materials`),

  // Stage Catalog
  getStageCatalog: () => api.get('/job-cards/stages/catalog'),

  // Order lookup
  getJobCardsByOrder: (orderId) => api.get(`/job-cards/by-order/${encodeURIComponent(orderId)}`),
};

// ==========================================
// ERROR HANDLER
// ==========================================
const handleApiError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);

  let message = defaultMessage;

  if (error.response) {
    message = error.response.data?.message ||
      error.response.data?.error ||
      `Server error: ${error.response.status}`;

    switch (error.response.status) {
      case 400:
        message = 'Bad request: ' + message;
        break;
      case 401:
        message = 'Unauthorized: Please login again';
        logout({ remote: true });
        window.location.href = '/login';
        break;
      case 403:
        message = 'Forbidden: You don\'t have permission';
        break;
      case 404:
        message = 'Resource not found';
        break;
      case 409:
        message = 'Conflict: ' + message;
        break;
      case 500:
        message = 'Server error: Please try again later';
        break;
      default:
        break;
    }
  } else if (error.request) {
    message = 'No response from server. Please check your connection.';
  } else {
    message = error.message || defaultMessage;
  }

  alert(message);
  return message;
};

// ==========================================
// CONSTANTS & CONFIGURATION
// ==========================================
const STAGE_CONFIG = {
  PRE_CASTING: { color: 'orange', label: 'Pre-Casting', desc: 'Wax & Tree' },
  CASTING: { color: 'purple', label: 'Casting', desc: 'Molding & Metal' },
  POST_CASTING: { color: 'blue', label: 'Post-Casting', desc: 'Finishing & QC' }
};

const formatCurrency = (value) => `Rs.${Number(value || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;

// ==========================================
// SUB-COMPONENTS
// ==========================================

const StageItem = ({ stage }) => (
  <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg mb-2 bg-white/80">
    <div className="flex-1 min-w-0">
      <div className="font-medium text-slate-800 text-sm">{stage.label || stage.stage_name}</div>
      <div className="text-xs text-slate-500 font-mono">{stage.stage_code}</div>
    </div>
    {stage.qc_required && (
      <span className="text-[10px] px-2 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-700 font-medium">QC</span>
    )}
  </div>
);

const TimelineStage = ({ stage }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'LOCKED': return 'bg-slate-50 border-slate-200 text-slate-400';
      case 'OPEN': return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'PENDING': return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'IN_PROGRESS': return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'COMPLETED': return 'bg-green-50 border-green-200 text-green-700';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'LOCKED': return <Lock size={14} />;
      case 'OPEN':
      case 'PENDING': return <Clock size={14} />;
      case 'IN_PROGRESS': return <User size={14} />;
      case 'COMPLETED': return <CheckCircle2 size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  return (
    <div className={`p-3 rounded-lg border flex items-center gap-3 transition-all ${getStatusColor(stage.status)}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-white/50 border shadow-sm shrink-0`}>
        {getStatusIcon(stage.status)}
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-xs">{stage.stage_name}</h4>
        {stage.qc_required && <div className="text-[10px] opacity-80 flex items-center gap-1"><CheckSquare size={8} /> QC Req</div>}
      </div>
      <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-white/50 rounded-full">{stage.status}</span>
    </div>
  );
};

const StageManagerModal = ({ isOpen, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    stage_name: '',
    stage_type: 'PRE_CASTING',
    expected_duration_minutes: 60
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        stage_name: '',
        stage_type: 'PRE_CASTING',
        expected_duration_minutes: 60
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!formData.stage_name.trim()) {
      alert('Stage Name is required');
      return;
    }
    if (!formData.expected_duration_minutes || Number(formData.expected_duration_minutes) <= 0) {
      alert('Expected Duration must be greater than 0');
      return;
    }

    onSave({
      ...formData,
      stage_code: formData.stage_name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '')
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-0 backdrop-blur-sm sm:p-4">
      <div className="flex h-full w-full flex-col bg-white text-slate-900 shadow-2xl sm:h-auto sm:max-w-[600px] sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-5">
          <div>
            <h3 className="text-lg font-bold">Add Production Stage</h3>
            <p className="text-xs text-slate-400">Append a custom stage to this job card workflow.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Stage Name</label>
            <input
              type="text"
              value={formData.stage_name}
              onChange={(e) => setFormData((prev) => ({ ...prev, stage_name: e.target.value }))}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-900 outline-none"
              placeholder="Ex: Laser Cleaning"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Stage Type</label>
              <select
                value={formData.stage_type}
                onChange={(e) => setFormData((prev) => ({ ...prev, stage_type: e.target.value }))}
                className="mt-2 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-900 outline-none"
              >
                <option value="PRE_CASTING">Pre-Casting</option>
                <option value="POST_CASTING">Post-Casting</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Expected Duration</label>
              <input
                type="number"
                min="1"
                value={formData.expected_duration_minutes}
                onChange={(e) => setFormData((prev) => ({ ...prev, expected_duration_minutes: e.target.value }))}
                className="mt-2 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-900 outline-none"
                placeholder="Minutes"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 p-5">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
          <ApiButton onClick={handleSubmit} loading={saving} className="px-5 py-2 text-sm font-semibold rounded-lg">
            <span className="inline-flex items-center gap-2"><Plus size={16} /> Save Stage</span>
          </ApiButton>
        </div>
      </div>
    </div>
  );
};

const JobCardStageManager = ({ jobCardId, stages, onRefresh }) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const groupedStages = [
    { key: 'pre_casting', label: 'Pre-Casting' },
    { key: 'casting', label: 'Casting' },
    { key: 'post_casting', label: 'Post-Casting' }
  ];

  const handleAddStage = async (payload) => {
    setSaving(true);
    try {
      await productionApi.addJobCardStage(jobCardId, payload);
      setIsAddOpen(false);
      await onRefresh();
    } catch (error) {
      handleApiError(error, 'Failed to add stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStage = async (stage) => {
    const confirmed = window.confirm(`Delete stage ${stage.stage_name}? This cannot be undone if production has started.`);
    if (!confirmed) return;

    try {
      await productionApi.deleteJobCardStage(stage.stage_id);
      await onRefresh();
    } catch (error) {
      handleApiError(error, 'Failed to delete stage');
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <StageManagerModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onSave={handleAddStage} saving={saving} />
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
        <div>
          <h3 className="text-base font-bold text-slate-800">Stage Manager</h3>
          <p className="text-xs text-slate-500">Add custom stages or delete stages that have not started.</p>
        </div>
        <button onClick={() => setIsAddOpen(true)} className="flex items-center gap-2 rounded-lg bg-[#3B82F6] px-4 py-2 text-sm font-semibold text-white">
          <Plus size={16} /> Add Stage
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 p-6 lg:grid-cols-3">
        {groupedStages.map((group) => (
          <div key={group.key} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">{group.label}</div>
            <div className="space-y-2">
              {(stages?.[group.key] || []).map((stage) => (
                <div key={stage.stage_id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-800">{stage.stage_name}</div>
                    <div className="text-[11px] text-slate-500">
                      {stage.stage_code} • Seq {stage.sequence_number || stage.sequence_no} • {stage.status}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteStage(stage)}
                    disabled={!stage.is_deletable}
                    title={stage.is_deletable ? 'Delete stage' : 'Only LOCKED or PENDING stages can be deleted'}
                    className="rounded-lg p-2 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-slate-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {(stages?.[group.key] || []).length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-xs text-slate-400">
                  No stages in this section.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// SCREEN 1: DESIGN MASTER (READ ONLY)
// ==========================================

const DesignMasterScreen = () => {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDesigns();
  }, []);

  const loadDesigns = async () => {
    setLoading(true);
    try {
      const res = await productionApi.getActiveDesigns();
      if (res.data.success) {
        setDesigns(res.data.data);
      } else {
        console.error('Failed to load designs:', res.data.message);
      }
    } catch (e) {
      handleApiError(e, 'Failed to load designs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col">
      <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div>
          <h2 className="text-xl font-bold text-slate-800 tracking-tight">Design Master</h2>
          <p className="text-sm text-slate-500 mt-1">View active design blueprints</p>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-0 mobile-scroll-table">
        <table className="w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-slate-700 font-semibold uppercase text-xs tracking-wider sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-4">Image</th>
              <th className="px-6 py-4">Code</th>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Category</th>
              <th className="px-6 py-4">Dept</th>
              <th className="px-6 py-4 text-center">Weight</th>
              <th className="px-6 py-4 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="7" className="p-4">
                  <SkeletonLoader count={4} height={56} />
                </td>
              </tr>
            ) : designs.map((design) => (
              <tr key={design.design_id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3">
                  <div className="w-10 h-10 rounded bg-slate-100 overflow-hidden border border-slate-200">
                    {design.image ? <img src={design.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={16} /></div>}
                  </div>
                </td>
                <td className="px-6 py-4 font-bold text-indigo-900">{design.design_code}</td>
                <td className="px-6 py-4 font-medium text-slate-800">{design.design_name}</td>
                <td className="px-6 py-4"><span className="px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-medium">{design.category_name || design.category || '-'}</span></td>
                <td className="px-6 py-4">{design.department_name || design.department || '-'}</td>
                <td className="px-6 py-4 text-center font-mono">{design.actual_weight}g</td>
                <td className="px-6 py-4 text-center">
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">{design.status}</span>
                </td>
              </tr>
            ))}
            {!loading && designs.length === 0 && <tr><td colSpan="7" className="px-6 py-10 text-center text-slate-400 italic">No active designs found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==========================================
// SCREEN 2: JOB CARDS (with Grouped Order ID functionality)
// ==========================================

const JobCardList = ({ onCreate, onView, orderFilter, onClearOrderFilter }) => {
  const [groupedJobs, setGroupedJobs] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchJobCards = async () => {
      setLoading(true);
      try {
        const res = await productionApi.getJobCards();
        if (res.data.success) {
          // Group jobs by order_id
          const grouped = res.data.data.reduce((acc, job) => {
            const orderId = job.order_id || 'UNASSIGNED';
            if (!acc[orderId]) {
              acc[orderId] = [];
            }
            acc[orderId].push(job);
            return acc;
          }, {});

          setGroupedJobs(grouped);

          // Auto-expand all orders by default
          const expanded = Object.keys(grouped).reduce((acc, orderId) => {
            acc[orderId] = true;
            return acc;
          }, {});
          setExpandedOrders(expanded);
        }
      } catch (err) {
        handleApiError(err, 'Failed to fetch job cards');
      } finally {
        setLoading(false);
      }
    };

    fetchJobCards();
  }, []);

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'OPEN':
        return 'bg-blue-100 text-blue-700';
      case 'QC_PENDING':
        return 'bg-amber-100 text-amber-700';
      case 'IN_PROGRESS':
        return 'bg-purple-100 text-purple-700';
      case 'COMPLETED':
        return 'bg-green-100 text-green-700';
      case 'ON_HOLD':
        return 'bg-red-100 text-red-700';
      case 'READY_FOR_FINAL_QC':
        return 'bg-amber-100 text-amber-700';
      case 'QC_APPROVED':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const orderFilteredJobs = orderFilter
    ? Object.fromEntries(
      Object.entries(groupedJobs).filter(([orderId]) => orderId === orderFilter)
    )
    : groupedJobs;

  const visibleGroupedJobs = !normalizedSearch
    ? orderFilteredJobs
    : Object.fromEntries(
      Object.entries(orderFilteredJobs)
        .map(([orderId, jobs]) => {
          const orderMatches = String(orderId).toLowerCase().includes(normalizedSearch);
          if (orderMatches) {
            return [orderId, jobs];
          }

          const matchingJobs = jobs.filter((job) => (
            String(job.job_card_no || '').toLowerCase().includes(normalizedSearch) ||
            String(job.order_id || '').toLowerCase().includes(normalizedSearch) ||
            String(job.sales_order_code || '').toLowerCase().includes(normalizedSearch) ||
            String(job.design_code || '').toLowerCase().includes(normalizedSearch) ||
            String(job.customer_name || '').toLowerCase().includes(normalizedSearch)
          ));

          return [orderId, matchingJobs];
        })
        .filter(([, jobs]) => jobs.length > 0)
    );

  const OrderGroupRow = ({ orderId, jobs }) => {
    const isExpanded = expandedOrders[orderId];
    const firstJob = jobs[0];
    const totalQuantity = jobs.reduce((sum, job) => sum + job.quantity, 0);
    const jobCount = jobs.length;

    // Determine overall status for the order group
    const statuses = jobs.map(j => j.status);
    let groupStatus = 'OPEN';
    if (statuses.every(s => s === 'COMPLETED')) groupStatus = 'COMPLETED';
    else if (statuses.some(s => s === 'QC_PENDING')) groupStatus = 'QC_PENDING';
    else if (statuses.some(s => s === 'IN_PROGRESS')) groupStatus = 'IN_PROGRESS';

    return (
      <>
        {/* Order Group Header */}
        <tr
          className="bg-slate-100 hover:bg-slate-200 cursor-pointer border-t-2 border-b border-slate-200 font-medium"
          onClick={() => toggleOrder(orderId)}
        >
          <td className="px-6 py-4 font-bold text-indigo-900" colSpan="2">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronDown size={18} className="text-indigo-600" /> : <ChevronUp size={18} className="text-indigo-600" />}
              <span className="text-sm font-bold">Order ID: <span className="font-mono text-indigo-700 bg-indigo-50 px-2 py-1 rounded-md">{orderId}</span></span>
            </div>
          </td>
          <td className="px-6 py-4">
            <span className="text-xs font-semibold bg-indigo-200 text-indigo-800 px-3 py-1.5 rounded-full">
              {jobCount} Job Card{jobCount > 1 ? 's' : ''}
            </span>
          </td>
          <td className="px-6 py-4 text-slate-500 text-xs" colSpan="2">
            {firstJob.customer_name ? (
              <span className="font-medium text-slate-700">{firstJob.customer_name}</span>
            ) : null}
            {firstJob.sales_order_code ? (
              <span className="ml-2 font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded text-xs">{firstJob.sales_order_code}</span>
            ) : null}
          </td>
          <td className="px-6 py-4 text-center font-mono text-sm font-bold">
            {totalQuantity}
          </td>
          <td className="px-6 py-4 text-sm text-slate-600">
            {formatDate(firstJob.expected_delivery_date)}
          </td>
          <td className="px-6 py-4 text-center">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${getStatusColor(groupStatus)}`}>
              {groupStatus}
            </span>
          </td>
          <td className="px-6 py-4 text-right">
            <span className="text-xs text-slate-400 bg-white px-2 py-1 rounded-full">
              {isExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
          </td>
        </tr>

        {/* Individual Job Cards for this Order (visible when expanded) */}
        {isExpanded && jobs.map((job, index) => (
          <tr
            key={job.job_card_id}
            onClick={() => onView(job.job_card_id)}
            className="hover:bg-slate-50 cursor-pointer group transition-colors bg-white border-b border-slate-100"
          >
            <td className="px-6 py-4 pl-12 font-bold text-indigo-900 group-hover:text-indigo-600 text-sm">
              {job.job_card_no}
            </td>
            <td className="px-6 py-4 text-slate-600 text-sm">
              {job.order_id || '-'}
            </td>
            <td className="px-6 py-4 font-medium text-slate-800 text-sm">
              {job.design_code}
            </td>
            <td className="px-6 py-4 text-sm text-slate-600">
              {job.customer_name || <span className="text-slate-300">—</span>}
            </td>
            <td className="px-6 py-4 text-sm">
              {job.sales_order_code
                ? <span className="font-mono text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded text-xs">{job.sales_order_code}</span>
                : <span className="text-slate-300">—</span>}
            </td>
            <td className="px-6 py-4 text-center font-mono text-sm">
              {job.quantity}
            </td>
            <td className="px-6 py-4 text-sm">
              {formatDate(job.expected_delivery_date)}
            </td>
            <td className="px-6 py-4 text-center">
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(job.status)}`}>
                {job.status || 'OPEN'}
              </span>
            </td>
            <td className="px-6 py-4 text-right">
              <ArrowRight size={16} className="ml-auto text-slate-300 group-hover:text-indigo-600" />
            </td>
          </tr>
        ))}

        {/* Empty row for spacing if expanded */}
        {isExpanded && jobs.length > 0 && (
          <tr className="h-1 bg-transparent"><td colSpan="9"></td></tr>
        )}
      </>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Job Cards</h2>
          <p className="text-sm text-slate-500 mt-1">Production Orders grouped by Order ID</p>
          {orderFilter && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
              <span>Filtered by order `{orderFilter}`</span>
              <button onClick={onClearOrderFilter} className="font-semibold text-indigo-700 hover:text-indigo-900">
                Clear
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-80 max-w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search order ID or job card no..."
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-9 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            onClick={onCreate}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg shadow-sm font-medium transition-all hover:shadow-md"
          >
            <Plus size={18} /> Create Job Card
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto mobile-scroll-table">
        <table className="w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-slate-700 font-semibold uppercase text-xs tracking-wider sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-4">Job No</th>
              <th className="px-6 py-4">Order ID</th>
              <th className="px-6 py-4">Design Code</th>
              <th className="px-6 py-4">Customer</th>
              <th className="px-6 py-4">Sales Order</th>
              <th className="px-6 py-4 text-center">Qty</th>
              <th className="px-6 py-4">Delivery</th>
              <th className="px-6 py-4 text-center">Status</th>
              <th className="px-6 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="9" className="p-4">
                  <SkeletonLoader count={5} height={52} />
                </td>
              </tr>
            ) : Object.keys(visibleGroupedJobs).length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Package size={48} className="text-slate-300" />
                    <span className="text-slate-400 italic text-lg">No job cards found</span>
                    <button
                      onClick={onCreate}
                      className="mt-2 text-indigo-600 hover:text-indigo-700 font-medium text-sm flex items-center gap-1"
                    >
                      <Plus size={16} /> Create your first job card
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              // Sort order IDs for consistent display
              Object.keys(visibleGroupedJobs)
                .sort((a, b) => {
                  if (a === 'UNASSIGNED') return 1;
                  if (b === 'UNASSIGNED') return -1;
                  return a.localeCompare(b);
                })
                .map(orderId => (
                  <OrderGroupRow
                    key={orderId}
                    orderId={orderId}
                    jobs={visibleGroupedJobs[orderId]}
                  />
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary footer */}
      {!loading && Object.keys(visibleGroupedJobs).length > 0 && (
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 flex justify-between items-center">
          <div>
            <span className="font-medium text-slate-700">Summary:</span> {Object.keys(visibleGroupedJobs).length} Order ID{Object.keys(visibleGroupedJobs).length > 1 ? 's' : ''} • {Object.values(visibleGroupedJobs).flat().length} Total Job Cards
          </div>
          <div className="flex gap-4">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Open: {Object.values(visibleGroupedJobs).flat().filter(j => j.status === 'OPEN').length}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> QC Pending: {Object.values(visibleGroupedJobs).flat().filter(j => j.status === 'QC_PENDING').length}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const JobCardCreate = ({ onCancel, onSuccess, initialData = {} }) => {
  const [designs, setDesigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [fullDesignDetails, setFullDesignDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState(initialData.mode || 'single');
  const [formData, setFormData] = useState({
    order_id: initialData.order_id || '',
    quantity: initialData.quantity || '',
    quantity_per_card: initialData.quantity_per_card || 1,
    count: initialData.count || '',
    expected_delivery_date: initialData.expected_delivery_date || '',
    customer_id: initialData.customer_id || '',
    remarks: initialData.remarks || '',
    sales_order_id: initialData.sales_order_id || ''
  });
  const [batchItems, setBatchItems] = useState(initialData.batchItems || []);
  const [errors, setErrors] = useState({ order_id: '' });
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [orderContext, setOrderContext] = useState(null);
  const [orderContextLoading, setOrderContextLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [materialsCatalog, setMaterialsCatalog] = useState([]);
  const [stageMaterialAssignments, setStageMaterialAssignments] = useState({});
  const [expandedStageSections, setExpandedStageSections] = useState({});

  useEffect(() => {
    const fetchDesigns = async () => {
      setLoadingDesigns(true);
      try {
        const [designRes, lookupRes, materialsRes] = await Promise.all([
          productionApi.getDesigns(),
          productionApi.getLookups(),
          productionApi.getMaterialsWithStock()
        ]);
        if (designRes.data.success) {
          setDesigns(designRes.data.data);
          if (initialData.design_id) {
            const preselected = designRes.data.data.find(d => d.design_id === initialData.design_id);
            if (preselected) {
              handleSelectDesign(preselected);
            }
          }
        }
        if (lookupRes.data.success) {
          setCustomers(lookupRes.data.data?.customers || []);
        }
        setMaterialsCatalog(materialsRes.data?.data || materialsRes.data?.materials || []);
      } catch (err) {
        handleApiError(err, 'Failed to load designs');
      } finally {
        setLoadingDesigns(false);
      }
    };

    fetchDesigns();
  }, [initialData.design_id]);

  useEffect(() => {
    const trimmed = formData.order_id.trim();
    if (!trimmed) { setOrderContext(null); return; }
    const timer = setTimeout(async () => {
      setOrderContextLoading(true);
      try {
        const res = await productionApi.getJobCardsByOrder(trimmed);
        if (res.data.success && res.data.data?.length > 0) {
          setOrderContext(res.data.data);
        } else {
          setOrderContext([]);
        }
      } catch {
        setOrderContext(null);
      } finally {
        setOrderContextLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.order_id]);

  const handleSelectDesign = async (design) => {
    setSelectedDesign(design);
    setLoadingDetails(true);
    try {
      const res = await productionApi.getDesignById(design.design_id);
      if (res.data.success) {
        setFullDesignDetails(res.data.data);
      }
    } catch (e) {
      handleApiError(e, 'Failed to load design details');
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    const stageGroups = fullDesignDetails?.stages || {};
    const nextAssignments = {};
    const nextExpanded = {};

    Object.values(stageGroups).forEach((group) => {
      (group || []).forEach((stage) => {
        const rows = (stage.default_materials || []).map((mat) => ({
          material_id: String(mat.material_id || ''),
          quantity: String(mat.qty ?? mat.quantity ?? ''),
          unit: mat.unit || 'g'
        }));
        nextAssignments[stage.stage_code] = rows.length > 0
          ? rows
          : [{ material_id: '', quantity: '', unit: 'g' }];
        nextExpanded[stage.stage_code] = rows.length > 0;
      });
    });

    setStageMaterialAssignments(nextAssignments);
    setExpandedStageSections(nextExpanded);
  }, [fullDesignDetails?.design_id, fullDesignDetails?.stages]);

  const addSelectedDesignToBatch = () => {
    if (!selectedDesign) return;

    setBatchItems(prev => {
      const existing = prev.find(item => item.design_id === selectedDesign.design_id);
      if (existing) {
        return prev.map(item =>
          item.design_id === selectedDesign.design_id
            ? { ...item, quantity: item.quantity || 1 }
            : item
        );
      }

      return [
        ...prev,
        {
          design_id: selectedDesign.design_id,
          design_code: selectedDesign.design_code,
          design_name: selectedDesign.design_name,
          quantity: 1
        }
      ];
    });
  };

  const updateBatchItemQuantity = (designId, quantity) => {
    setBatchItems(prev => prev.map(item => (
      item.design_id === designId
        ? { ...item, quantity }
        : item
    )));
  };

  const removeBatchItem = (designId) => {
    setBatchItems(prev => prev.filter(item => item.design_id !== designId));
  };

  const handleSubmit = async () => {
    const newErrors = { order_id: '' };
    let hasError = false;

    if (!formData.order_id || formData.order_id.trim() === '') {
      newErrors.order_id = 'Order ID is required';
      hasError = true;
    }

    if (mode === 'single' && (!formData.quantity || Number(formData.quantity) < 1)) {
      newErrors.quantity = 'Quantity must be at least 1';
      hasError = true;
    }

    if (mode === 'batch' && batchItems.length < 1) {
      newErrors.batch_items = 'Add at least one design to the batch';
      hasError = true;
    }

    if (mode === 'batch') {
      const invalidItem = batchItems.find(item => !item.quantity || Number(item.quantity) < 1);
      if (invalidItem) {
        newErrors.batch_items = 'Each selected design must have quantity at least 1';
        hasError = true;
      }
    }

    setErrors(newErrors);

    if (hasError || (mode === 'single' && !selectedDesign)) return;

    setSubmitting(true);
    try {
      const normalizedStageMaterials = Object.entries(stageMaterialAssignments || {}).reduce((acc, [stageCode, rows]) => {
        const materials = (rows || [])
          .map((row) => ({
            material_id: Number(row.material_id),
            quantity: Number(row.quantity),
            unit: row.unit || 'g'
          }))
          .filter((row) => row.material_id > 0 && row.quantity > 0);
        if (materials.length > 0) {
          acc[stageCode] = materials;
        }
        return acc;
      }, {});

      const payload = {
        design_id: selectedDesign.design_id,
        order_id: formData.order_id.trim(),
        expected_delivery_date: formData.expected_delivery_date || null,
        customer_id: formData.customer_id || null,
        sales_order_id: formData.sales_order_id || null,
        remarks: formData.remarks || '',
        ...(mode === 'single' ? { stage_materials: normalizedStageMaterials } : {})
      };
      const res = mode === 'batch'
        ? await productionApi.createBatchJobCards({
          ...payload,
          items: batchItems.map(item => ({
            design_id: item.design_id,
            quantity: parseInt(item.quantity, 10)
          }))
        })
        : await productionApi.createJobCard({
          ...payload,
          quantity: parseInt(formData.quantity, 10)
        });
      if (res.data.success) {
        onSuccess({
          type: mode,
          orderId: mode === 'batch' ? res.data.order_id : (res.data.data?.order_id || formData.order_id.trim()),
          createdCount: mode === 'batch' ? res.data.created_count : 1,
          firstJobCardNo: mode === 'batch' ? res.data.job_cards?.[0]?.job_card_no : res.data.data?.job_card_no,
          lastJobCardNo: mode === 'batch' ? res.data.job_cards?.[res.data.job_cards.length - 1]?.job_card_no : res.data.data?.job_card_no
        });
      }
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      alert(`Failed to create job card: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredDesigns = designs.filter(d =>
    d.design_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.design_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const workflowStages = ['pre_casting', 'casting', 'post_casting'].flatMap(
    (key) => fullDesignDetails?.stages?.[key] || []
  );
  const materialLookup = new Map(materialsCatalog.map((item) => [String(item.material_id), item]));

  const updateStageMaterialRow = (stageCode, index, field, value) => {
    setStageMaterialAssignments((prev) => {
      const currentRows = prev[stageCode] || [{ material_id: '', quantity: '', unit: 'g' }];
      const nextRows = [...currentRows];
      nextRows[index] = { ...nextRows[index], [field]: value };
      if (field === 'material_id') {
        const selected = materialLookup.get(String(value));
        nextRows[index].unit = selected?.unit || nextRows[index].unit || 'g';
      }
      return { ...prev, [stageCode]: nextRows };
    });
  };

  const addStageMaterialRow = (stageCode) => {
    setStageMaterialAssignments((prev) => ({
      ...prev,
      [stageCode]: [...(prev[stageCode] || []), { material_id: '', quantity: '', unit: 'g' }]
    }));
    setExpandedStageSections((prev) => ({ ...prev, [stageCode]: true }));
  };

  const removeStageMaterialRow = (stageCode, index) => {
    setStageMaterialAssignments((prev) => {
      const filteredRows = (prev[stageCode] || []).filter((_, rowIndex) => rowIndex !== index);
      return {
        ...prev,
        [stageCode]: filteredRows.length > 0 ? filteredRows : [{ material_id: '', quantity: '', unit: 'g' }]
      };
    });
  };

  const toggleStageAssignmentSection = (stageCode) => {
    setExpandedStageSections((prev) => ({ ...prev, [stageCode]: !prev[stageCode] }));
  };

  return (
    <div className="bg-white rounded-xl shadow-xl border border-slate-200 flex flex-col h-full">
      {initialData.fromSalesOrder && (
        <div className="px-6 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center gap-2 text-sm text-indigo-800">
          <span className="font-bold">Creating job card for Sales Order:</span>
          <span className="font-mono bg-indigo-100 px-2 py-0.5 rounded text-indigo-900">{initialData.sales_order_id || initialData.order_id}</span>
          <span className="ml-1 text-indigo-500">— design, quantity and customer have been pre-filled.</span>
        </div>
      )}
      <div className="px-6 py-4 border-b flex justify-between items-center bg-gradient-to-r from-slate-50 to-white">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Create Job Card</h2>
          <p className="text-sm text-slate-500">Select active design to auto-fill details</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
          <ApiButton
            onClick={handleSubmit}
            loading={submitting}
            variant="success"
            disabled={!formData.order_id || (mode === 'single' ? (!selectedDesign || !formData.quantity) : batchItems.length === 0)}
            className="px-6 py-2 rounded-lg font-medium shadow-sm"
          >
            <span className="inline-flex items-center gap-2"><Factory size={18} /> {mode === 'batch' ? `Create ${batchItems.length} Job Cards` : 'Create Job'}</span>
          </ApiButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 bg-slate-50">
        <div className="max-w-6xl mx-auto grid grid-cols-12 gap-6">

          <div className="col-span-5 bg-white p-6 rounded-xl border border-slate-200 shadow-sm h-fit">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Search size={18} className="text-indigo-600" /> Select Design Master
            </h3>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-3 text-slate-400" size={16} />
              <input
                className="w-full pl-9 p-2.5 border rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                placeholder="Search Code or Name..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
              {loadingDesigns ? (
                <SkeletonLoader count={4} height={68} />
              ) : filteredDesigns.map(d => (
                <div
                  key={d.design_id}
                  onClick={() => handleSelectDesign(d)}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${selectedDesign?.design_id === d.design_id ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'hover:border-indigo-300 hover:bg-slate-50'}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-indigo-900 text-sm">{d.design_code}</div>
                      <div className="text-xs text-slate-600">{d.design_name}</div>
                    </div>
                    {selectedDesign?.design_id === d.design_id && <CheckCircle2 size={16} className="text-indigo-600" />}
                  </div>
                  <div className="mt-2 text-[10px] text-slate-400 font-mono">Wt: {d.actual_weight}g</div>
                </div>
              ))}
              {!loadingDesigns && filteredDesigns.length === 0 && (
                <div className="text-center text-sm text-slate-400 py-4">No designs found</div>
              )}
            </div>
          </div>

          <div className="col-span-7 space-y-6">
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <div className="mb-5 inline-flex rounded-lg border border-slate-200 bg-slate-100 p-1">
                <button
                  onClick={() => setMode('single')}
                  className={`rounded-md px-4 py-2 text-sm font-medium ${mode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                >
                  Single Job Card
                </button>
                <button
                  onClick={() => setMode('batch')}
                  className={`rounded-md px-4 py-2 text-sm font-medium ${mode === 'batch' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                >
                  Batch Job Cards
                </button>
              </div>
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <ClipboardList size={18} className="text-indigo-600" /> Order Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Order ID *</label>
                  <input
                    type="text"
                    className={`w-full mt-1 p-2.5 border rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 ${errors.order_id ? 'border-red-500 focus:border-red-500' : ''}`}
                    value={formData.order_id}
                    onChange={e => setFormData({ ...formData, order_id: e.target.value })}
                    placeholder="Enter Order ID"
                  />
                  {errors.order_id && <p className="text-red-500 text-xs mt-1">{errors.order_id}</p>}
                  {orderContextLoading && (
                    <p className="text-xs text-indigo-500 mt-1 animate-pulse">Looking up order...</p>
                  )}
                  {!orderContextLoading && orderContext !== null && (
                    <div className={`mt-2 p-2.5 rounded-lg border text-xs ${orderContext.length > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
                      {orderContext.length > 0 ? (
                        <>
                          <span className="font-bold">This order already has {orderContext.length} job card{orderContext.length > 1 ? 's' : ''}: </span>
                          {orderContext.map((jc, i) => (
                            <span key={jc.job_card_id}>
                              {jc.design_name || jc.design_code} ({jc.quantity} pcs){i < orderContext.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </>
                      ) : (
                        <span className="font-bold">New order — no existing job cards.</span>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Client</label>
                  <select
                    className="w-full mt-1 p-2.5 border rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                    value={formData.customer_id}
                    onChange={e => setFormData({ ...formData, customer_id: e.target.value })}
                  >
                    <option value="">Select client</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                </div>
                {mode === 'single' ? (
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Quantity *</label>
                    <input
                      type="number"
                      className={`w-full mt-1 p-2.5 border rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 ${errors.quantity ? 'border-red-500' : ''}`}
                      value={formData.quantity}
                      onChange={e => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="Enter quantity"
                    />
                    {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>}
                  </div>
                ) : (
                  <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs font-bold uppercase text-slate-500">Selected Designs</div>
                        <div className="text-sm text-slate-600">Add one design per job card for this order.</div>
                      </div>
                      <button
                        type="button"
                        onClick={addSelectedDesignToBatch}
                        disabled={!selectedDesign}
                        className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white disabled:bg-slate-300"
                      >
                        Add Selected Design
                      </button>
                    </div>
                    <div className="mt-4 space-y-3">
                      {batchItems.length === 0 && (
                        <div className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
                          Select a design on the left, then add it to this order batch.
                        </div>
                      )}
                      {batchItems.map(item => (
                        <div key={item.design_id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-800">{item.design_code}</div>
                            <div className="text-xs text-slate-500">{item.design_name}</div>
                          </div>
                          <div className="w-28">
                            <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Pieces</label>
                            <input
                              type="number"
                              min="1"
                              className="w-full rounded-lg border p-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                              value={item.quantity}
                              onChange={e => updateBatchItemQuantity(item.design_id, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeBatchItem(item.design_id)}
                            className="rounded-lg px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                    {errors.batch_items && <p className="mt-3 text-xs text-red-500">{errors.batch_items}</p>}
                  </div>
                )}
                <div className="col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase">Delivery Date</label>
                  <input
                    type="date"
                    className="w-full mt-1 p-2.5 border rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                    value={formData.expected_delivery_date}
                    onChange={e => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase">Notes</label>
                  <textarea
                    className="w-full mt-1 p-2.5 border rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 min-h-24"
                    value={formData.remarks}
                    onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              {mode === 'batch' && batchItems.length > 0 && (
                <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
                  This will create {batchItems.length} job cards for order {formData.order_id || '...'} across {batchItems.length} design{batchItems.length > 1 ? 's' : ''}.
                </div>
              )}
            </div>

            {selectedDesign && (
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Gem size={18} className="text-indigo-600" /> Master Design Details (Autofilled)
                </h3>

                <div className="flex gap-4 mb-6 bg-slate-50 p-4 rounded-lg border border-slate-100">
                  {fullDesignDetails?.image && (
                    <img src={fullDesignDetails.image} alt={`${selectedDesign.design_name} preview`} className="w-20 h-20 object-cover rounded bg-white border" />
                  )}
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm flex-1">
                    <div><span className="text-slate-400 text-xs">Code:</span> <span className="font-mono font-medium">{selectedDesign.design_code}</span></div>
                    <div><span className="text-slate-400 text-xs">Category:</span> <span className="font-medium">{fullDesignDetails?.category_name || fullDesignDetails?.category}</span></div>
                    <div><span className="text-slate-400 text-xs">Weight:</span> <span className="font-medium">{selectedDesign.actual_weight}g</span></div>
                    <div><span className="text-slate-400 text-xs">Stones:</span> <span className="font-medium">{(fullDesignDetails?.stones || []).length} types</span></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Workflow Stages Preview</div>
                  {loadingDetails ? (
                    <div className="text-sm text-indigo-600 animate-pulse flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                      Loading workflow...
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {['pre_casting', 'casting', 'post_casting'].map(key => (
                        <div key={key} className="border rounded bg-slate-50/50 p-2">
                          <div className={`text-[10px] font-bold uppercase mb-2 ${key === 'pre_casting' ? 'text-orange-600' : key === 'casting' ? 'text-purple-600' : 'text-blue-600'}`}>{STAGE_CONFIG[key.toUpperCase()].label}</div>
                          <div className="space-y-1">
                            {fullDesignDetails?.stages?.[key]?.map(s => <StageItem key={s.stage_code} stage={s} />)}
                            {(!fullDesignDetails?.stages?.[key] || fullDesignDetails?.stages[key].length === 0) && <div className="text-[10px] text-slate-400 italic">None</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {!loadingDetails && workflowStages.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Optional Stage Material Pre-Assignment</div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                      {workflowStages.map((stage) => {
                        const rows = stageMaterialAssignments[stage.stage_code] || [{ material_id: '', quantity: '', unit: 'g' }];
                        const activeRows = rows.filter((row) => Number(row.material_id) > 0 && Number(row.quantity) > 0);
                        const isExpanded = !!expandedStageSections[stage.stage_code];
                        return (
                          <div key={stage.stage_code} className="rounded-xl border border-slate-200 bg-white">
                            <button
                              type="button"
                              onClick={() => toggleStageAssignmentSection(stage.stage_code)}
                              className="flex w-full items-center justify-between px-4 py-3 text-left"
                            >
                              <div>
                                <div className="text-sm font-semibold text-slate-800">{stage.stage_name}</div>
                                <div className="text-[11px] text-slate-500">
                                  {activeRows.length > 0
                                    ? `${activeRows.length} reserved material${activeRows.length > 1 ? 's' : ''}`
                                    : 'No materials reserved yet'}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {activeRows.length > 0 && (
                                  <span className="rounded-full bg-indigo-50 px-2 py-1 text-[10px] font-bold text-indigo-700">
                                    {activeRows.length} items
                                  </span>
                                )}
                                {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                              </div>
                            </button>

                            {isExpanded && (
                              <div className="border-t border-slate-100 px-4 py-4 space-y-3">
                                {(rows || []).map((row, index) => {
                                  const stockRow = materialLookup.get(String(row.material_id));
                                  const availableQty = Number(stockRow?.available_quantity || 0);
                                  return (
                                    <div key={`${stage.stage_code}-${index}`} className="grid grid-cols-12 gap-3 items-start rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <div className="col-span-6">
                                        <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Material</label>
                                        <select
                                          className="w-full rounded-lg border border-slate-300 bg-white p-2 text-sm"
                                          value={row.material_id}
                                          onChange={(e) => updateStageMaterialRow(stage.stage_code, index, 'material_id', e.target.value)}
                                        >
                                          <option value="">Select material</option>
                                          {materialsCatalog.map((material) => (
                                            <option key={material.material_id} value={material.material_id}>
                                              {material.material_name} | {material.material_code || material.material_id}
                                            </option>
                                          ))}
                                        </select>
                                        <div className="mt-1 text-[11px] text-slate-500">
                                          Available stock: <span className={availableQty > 0 ? 'font-semibold text-emerald-700' : 'font-semibold text-rose-600'}>
                                            {availableQty} {stockRow?.unit || row.unit || 'g'}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="col-span-3">
                                        <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Quantity</label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.001"
                                          className="w-full rounded-lg border border-slate-300 bg-white p-2 text-sm"
                                          value={row.quantity}
                                          onChange={(e) => updateStageMaterialRow(stage.stage_code, index, 'quantity', e.target.value)}
                                          placeholder="0.000"
                                        />
                                      </div>
                                      <div className="col-span-2">
                                        <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Unit</label>
                                        <input
                                          type="text"
                                          className="w-full rounded-lg border border-slate-300 bg-slate-100 p-2 text-sm text-slate-600"
                                          value={row.unit || 'g'}
                                          readOnly
                                        />
                                      </div>
                                      <div className="col-span-1 flex h-full items-end justify-end">
                                        <button
                                          type="button"
                                          onClick={() => removeStageMaterialRow(stage.stage_code, index)}
                                          className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                          title="Remove material"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="flex items-center justify-between">
                                  <p className="text-[11px] text-slate-500">
                                    These rows are saved as stage reservations only. Inventory is checked and deducted when the stage actually starts.
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => addStageMaterialRow(stage.stage_code)}
                                    className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white"
                                  >
                                    Add Material
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CostBreakdownPanel = ({ costData, loading, jobStages }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center text-slate-500">
        Loading cost estimate...
      </div>
    );
  }

  if (!costData) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center text-slate-500">
        Cost estimate is not available.
      </div>
    );
  }

  const breakdownData = [
    { name: 'Material', value: Number(costData.material_cost || 0), fill: '#2563eb' },
    { name: 'Labor', value: Number(costData.labor_cost || 0), fill: '#0f766e' },
  ];
  const hasBreakdown = breakdownData.some((r) => r.value > 0);
  const profitLoss = Number(costData.profit_loss || 0);
  const profitPct = Number(costData.profit_margin_pct || 0);
  const isProfit = profitLoss >= 0;
  const flattenedStages = Object.values(jobStages || {}).flatMap((group) => Array.isArray(group) ? group : []);
  const stageMetaById = new Map(flattenedStages.map((stage) => [Number(stage.stage_id), stage]));
  const stageMetaByName = new Map(flattenedStages.map((stage) => [String(stage.stage_name || '').trim().toLowerCase(), stage]));

  const materialRows = (costData.material_breakdown || []).map((row, index) => {
    const rawMaterial = String(row.material || 'Material');
    const stageMatch = rawMaterial.match(/^Stage\s+(.+?)\s+-\s+(.+)$/i);
    const derivedStageName = row.stage || stageMatch?.[1] || 'Unknown';
    const derivedMaterialName = stageMatch?.[2] || rawMaterial;
    const matchedStage = stageMetaById.get(Number(row.stage_id)) || stageMetaByName.get(String(derivedStageName).trim().toLowerCase());
    return {
      ...row,
      key: `${row.stage_id || derivedStageName}-${derivedMaterialName}-${index}`,
      stage_id: Number(row.stage_id || matchedStage?.stage_id || 0) || null,
      stage: matchedStage?.stage_name || derivedStageName,
      material: derivedMaterialName,
      used: Number(row.used || 0),
      unit_cost: Number(row.unit_cost || 0),
      cost: Number(row.cost || 0),
    };
  });

  const laborRows = (costData.labor_breakdown || []).map((row, index) => {
    const matchedStage = stageMetaById.get(Number(row.stage_id)) || stageMetaByName.get(String(row.stage || '').trim().toLowerCase());
    return {
      ...row,
      key: `${row.stage_id || matchedStage?.stage_id || 'stage'}-${row.worker || 'worker'}-${index}`,
      stage_id: Number(row.stage_id || matchedStage?.stage_id || 0) || null,
      stage: matchedStage?.stage_name || row.stage || 'Unknown',
      hours: Number(row.hours || 0),
      days: Number(row.days || 0),
      cost: Number(row.cost || 0),
    };
  });

  const stageMaterialSections = (costData.stage_material_breakdown && costData.stage_material_breakdown.length > 0)
    ? costData.stage_material_breakdown
    : Array.from(materialRows.reduce((map, row) => {
        const key = row.stage_id || String(row.stage || 'unknown').toLowerCase();
        if (!map.has(key)) {
          const matchedStage = stageMetaById.get(Number(row.stage_id)) || stageMetaByName.get(String(row.stage || '').trim().toLowerCase());
          map.set(key, {
            stage_id: row.stage_id || matchedStage?.stage_id || null,
            stage_name: matchedStage?.stage_name || row.stage || 'Unknown',
            status: matchedStage?.status || 'UNKNOWN',
            items: [],
          });
        }
        map.get(key).items.push(row);
        return map;
      }, new Map()).values());

  const stageLaborSections = (costData.stage_labor_breakdown && costData.stage_labor_breakdown.length > 0)
    ? costData.stage_labor_breakdown
    : Array.from(laborRows.reduce((map, row) => {
        const key = row.stage_id || String(row.stage || 'unknown').toLowerCase();
        if (!map.has(key)) {
          const matchedStage = stageMetaById.get(Number(row.stage_id)) || stageMetaByName.get(String(row.stage || '').trim().toLowerCase());
          map.set(key, {
            stage_id: row.stage_id || matchedStage?.stage_id || null,
            stage_name: matchedStage?.stage_name || row.stage || 'Unknown',
            status: matchedStage?.status || 'UNKNOWN',
            items: [],
          });
        }
        map.get(key).items.push(row);
        return map;
      }, new Map()).values());

  const stageStatusRows = (costData.stage_statuses && costData.stage_statuses.length > 0)
    ? costData.stage_statuses
    : flattenedStages.map((stage) => ({
        stage_id: stage.stage_id,
        stage_name: stage.stage_name,
        sequence_no: stage.sequence_no || stage.sequence_number || 0,
        process_type: stage.process_type,
        status: stage.status,
        worker_name: stage.worker_name,
        actual_duration_minutes: Number(stage.actual_duration_minutes || 0),
        completed_at: stage.completed_at || null,
      }));
  const stageRows = (costData.per_stage || []).map((row) => ({
    ...row,
    material_cost: Number(row.material_cost || 0),
    labor_cost: Number(row.labor_cost || 0),
    machine_cost: Number(row.machine_cost || 0),
    stage_total: Number(row.stage_total || 0),
  }));
  const statusTone = (status) => {
    switch (String(status || '').toUpperCase()) {
      case 'COMPLETED':
      case 'QC_APPROVED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'QC_PENDING':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'IN_PROGRESS':
      case 'EXECUTION':
        return 'bg-sky-50 text-sky-700 border-sky-200';
      case 'REJECTED':
      case 'FAILED':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'LOCKED':
      case 'PENDING':
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 px-6 py-6 text-white shadow-sm">
        <div className="text-xs uppercase tracking-[0.2em] text-blue-200 font-bold">Cost Tracking</div>
        <div className="mt-2 text-3xl sm:text-4xl font-black">
          Total Cost: {formatCurrency(costData.total_cost)}
        </div>
        <div className="mt-2 text-sm text-blue-100">
          Live running estimate from completed stage logs.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Material Cost', value: formatCurrency(costData.material_cost), tone: 'bg-blue-50 text-blue-700 border-blue-200' },
          { label: 'Labor Cost', value: formatCurrency(costData.labor_cost), tone: 'bg-teal-50 text-teal-700 border-teal-200' },
          { label: 'Completion', value: `${Number(costData.completion_pct || 0).toFixed(1)}%`, tone: 'bg-slate-900 text-white border-slate-900' },
        ].map(card => (
          <div key={card.label} className={`rounded-xl border p-4 shadow-sm ${card.tone}`}>
            <div className="text-xs uppercase tracking-wider font-bold opacity-80">{card.label}</div>
            <div className="mt-3 text-2xl font-bold">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800">Cost Breakdown</h3>
            <span className="text-xs text-slate-400">{formatCurrency(costData.total_cost)}</span>
          </div>
          <div className="h-72">
            {hasBreakdown ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={breakdownData} dataKey="value" nameKey="name" innerRadius={65} outerRadius={95} paddingAngle={2}>
                    {breakdownData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">No cost logs yet.</div>
            )}
          </div>
          <div className="space-y-2">
            {breakdownData.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }} />
                  <span>{item.name}</span>
                </div>
                <span className="font-semibold text-slate-800">{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800">Per-Stage Cost</h3>
            <span className="text-xs text-slate-400">{stageRows.length} stage(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2 font-semibold">Stage</th>
                  <th className="px-3 py-2 font-semibold">Material</th>
                  <th className="px-3 py-2 font-semibold">Labor</th>
                  <th className="px-3 py-2 font-semibold">Stage Total</th>
                </tr>
              </thead>
              <tbody>
                {stageRows.length === 0 && (
                  <tr>
                    <td className="px-3 py-4 text-slate-400" colSpan={4}>No stage cost logged yet.</td>
                  </tr>
                )}
                {stageRows.map((row) => (
                  <tr key={row.stage_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-700">{row.stage_name}</td>
                    <td className="px-3 py-2">{formatCurrency(row.material_cost)}</td>
                    <td className="px-3 py-2">{formatCurrency(row.labor_cost)}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{formatCurrency(row.stage_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">Labor Utilization</h3>
          <span className="text-xs text-slate-400">{laborRows.length} row(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2 font-semibold">Stage</th>
                <th className="px-3 py-2 font-semibold">Worker</th>
                <th className="px-3 py-2 font-semibold">Hours</th>
                <th className="px-3 py-2 font-semibold">Days</th>
                <th className="px-3 py-2 font-semibold">Labor Cost</th>
              </tr>
            </thead>
            <tbody>
              {laborRows.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-slate-400" colSpan={5}>No labor cost logged yet. If this is an older job card, open the cost tracking again after backend restart or after stage worker contributions are saved.</td>
                </tr>
              )}
              {laborRows.map((row) => (
                <tr key={row.key} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-700">{row.stage || 'Unknown'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.worker || 'Assigned Worker'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.hours.toFixed(2)}</td>
                  <td className="px-3 py-2 text-slate-700">{row.days.toFixed(2)}</td>
                  <td className="px-3 py-2 font-semibold text-slate-900">{formatCurrency(row.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">All Materials Used</h3>
          <span className="text-xs text-slate-400">{materialRows.length} row(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2 font-semibold">Stage</th>
                <th className="px-3 py-2 font-semibold">Material</th>
                <th className="px-3 py-2 font-semibold">Used Qty</th>
                <th className="px-3 py-2 font-semibold">Unit Cost</th>
                <th className="px-3 py-2 font-semibold">Cost</th>
              </tr>
            </thead>
            <tbody>
              {materialRows.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-slate-400" colSpan={5}>No material consumption logged yet.</td>
                </tr>
              )}
              {materialRows.map((row) => (
                <tr key={row.key} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-700">{row.stage || 'Unknown'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.material || 'Material'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.used.toFixed(3)}</td>
                  <td className="px-3 py-2 text-slate-700">{formatCurrency(row.unit_cost)}</td>
                  <td className="px-3 py-2 font-semibold text-slate-900">{formatCurrency(row.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800">Stage-wise Materials</h3>
            <span className="text-xs text-slate-400">{stageMaterialSections.length} stage(s)</span>
          </div>
          <div className="space-y-4">
            {stageMaterialSections.length === 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400 text-center">
                No stage-wise material rows available.
              </div>
            )}
            {stageMaterialSections.map((section) => (
              <div key={`mat-${section.stage_id}`} className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between gap-3 bg-slate-50 px-4 py-3">
                  <div className="font-semibold text-slate-800">{section.stage_name}</div>
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusTone(section.status)}`}>{section.status || 'PENDING'}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2 font-semibold">Material</th>
                        <th className="px-3 py-2 font-semibold">Used Qty</th>
                        <th className="px-3 py-2 font-semibold">Unit Cost</th>
                        <th className="px-3 py-2 font-semibold">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.items.map((item, index) => (
                        <tr key={`mat-item-${section.stage_id}-${index}`} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-700">{item.material}</td>
                          <td className="px-3 py-2 text-slate-700">{Number(item.used || 0).toFixed(3)}</td>
                          <td className="px-3 py-2 text-slate-700">{formatCurrency(item.unit_cost || 0)}</td>
                          <td className="px-3 py-2 font-semibold text-slate-900">{formatCurrency(item.cost || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800">Stage-wise Labor</h3>
            <span className="text-xs text-slate-400">{stageLaborSections.length} stage(s)</span>
          </div>
          <div className="space-y-4">
            {stageLaborSections.length === 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400 text-center">
                No stage-wise labor rows available yet.
              </div>
            )}
            {stageLaborSections.map((section) => (
              <div key={`labor-${section.stage_id}`} className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between gap-3 bg-slate-50 px-4 py-3">
                  <div className="font-semibold text-slate-800">{section.stage_name}</div>
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusTone(section.status)}`}>{section.status || 'PENDING'}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2 font-semibold">Worker</th>
                        <th className="px-3 py-2 font-semibold">Hours</th>
                        <th className="px-3 py-2 font-semibold">Days</th>
                        <th className="px-3 py-2 font-semibold">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.items.map((item, index) => (
                        <tr key={`labor-item-${section.stage_id}-${index}`} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-700">{item.worker || 'Assigned Worker'}</td>
                          <td className="px-3 py-2 text-slate-700">{Number(item.hours || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-slate-700">{Number(item.days || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 font-semibold text-slate-900">{formatCurrency(item.cost || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">Job Card Stage Status</h3>
          <span className="text-xs text-slate-400">{stageStatusRows.length} stage(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2 font-semibold">Seq</th>
                <th className="px-3 py-2 font-semibold">Stage</th>
                <th className="px-3 py-2 font-semibold">Process</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Worker</th>
                <th className="px-3 py-2 font-semibold">Duration</th>
                <th className="px-3 py-2 font-semibold">Completed At</th>
              </tr>
            </thead>
            <tbody>
              {stageStatusRows.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-slate-400" colSpan={7}>No stage status available.</td>
                </tr>
              )}
              {stageStatusRows.map((row) => (
                <tr key={`status-${row.stage_id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-700">{row.sequence_no || '-'}</td>
                  <td className="px-3 py-2 font-medium text-slate-700">{row.stage_name}</td>
                  <td className="px-3 py-2 text-slate-700">{String(row.process_type || '').replace(/_/g, ' ') || '-'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusTone(row.status)}`}>{row.status || 'PENDING'}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{row.worker_name || '-'}</td>
                  <td className="px-3 py-2 text-slate-700">{Number(row.actual_duration_minutes || 0)} min</td>
                  <td className="px-3 py-2 text-slate-700">{row.completed_at ? new Date(row.completed_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            <div className="text-xs uppercase font-bold text-slate-500">Quotation Amount</div>
            <div className="mt-1 text-xl font-bold text-slate-900">{formatCurrency(costData.quotation_amount)}</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            <div className="text-xs uppercase font-bold text-slate-500">Estimated Cost</div>
            <div className="mt-1 text-xl font-bold text-slate-900">{formatCurrency(costData.total_cost)}</div>
          </div>
          <div className={`rounded-lg border p-4 ${isProfit ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className={`text-xs uppercase font-bold ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}>Projected {isProfit ? 'Profit' : 'Loss'}</div>
            <div className={`mt-1 text-xl font-bold ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}>
              {isProfit ? '+' : '-'}{formatCurrency(Math.abs(profitLoss))} ({Math.abs(profitPct).toFixed(2)}%)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InventoryReconciliationPanel = ({ data, loading }) => {
  const status = data?.overall_flag || data?.reconciliation_status || 'BALANCED';
  const rows = data?.materials || [];

  const statusStyles = {
    BALANCED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    MINOR_DISCREPANCY: 'bg-orange-50 text-orange-700 border-orange-200',
    MAJOR_DISCREPANCY: 'bg-red-50 text-red-700 border-red-200',
  };

  const getRowClass = (row) => {
    const pct = Math.abs(Number(row?.discrepancy_pct || 0));
    if (pct > 10) return 'bg-red-50';
    if (pct > 5) return 'bg-orange-50';
    return 'bg-emerald-50';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="space-y-3">
          <SkeletonLoader count={6} height={52} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm text-center text-slate-500">
        No reconciliation data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-slate-800 uppercase">Overall Status</h3>
          <p className="text-xs text-slate-500 mt-1">Material issued vs consumed check</p>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${statusStyles[status] || statusStyles.BALANCED}`}>
          {status}
        </span>
      </div>

      {status === 'MAJOR_DISCREPANCY' && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm font-semibold">
          Contact Inventory Manager — large discrepancy detected.
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Material</th>
              <th className="px-4 py-3 text-left">UOM</th>
              <th className="px-4 py-3 text-right">Issued</th>
              <th className="px-4 py-3 text-right">Used</th>
              <th className="px-4 py-3 text-right">Returned</th>
              <th className="px-4 py-3 text-right">Wastage</th>
              <th className="px-4 py-3 text-right">Discrepancy</th>
              <th className="px-4 py-3 text-right">Flag</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, idx) => (
              <tr key={`${r.material_id || r.material_name}-${idx}`} className={getRowClass(r)}>
                <td className="px-4 py-3 font-semibold text-slate-800">{r.material_name}</td>
                <td className="px-4 py-3">{r.uom || '-'}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.issued || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.used || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.returned || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.wastage || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">
                  {Number(r.discrepancy || 0).toFixed(3)} ({Number(r.discrepancy_pct || 0).toFixed(2)}%)
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                    r.flag === 'MAJOR' ? 'bg-red-100 text-red-700' :
                    r.flag === 'MINOR' ? 'bg-orange-100 text-orange-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {r.flag}
                  </span>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                  No materials found for reconciliation.
                </td>
              </tr>
            )}
          </tbody>
          {!!data?.totals && (
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr className="font-bold text-slate-700">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3">-</td>
                <td className="px-4 py-3 text-right font-mono">{Number(data.totals.issued || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(data.totals.used || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(data.totals.returned || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(data.totals.wastage || 0).toFixed(3)}</td>
                <td className="px-4 py-3 text-right font-mono">
                  {Number(data.totals.discrepancy || 0).toFixed(3)} ({Number(data.totals.discrepancy_pct || 0).toFixed(2)}%)
                </td>
                <td className="px-4 py-3 text-right">{status}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

const JobCardOverview = ({ jobCardId, onBack, onDuplicate }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [costData, setCostData] = useState(null);
  const [costLoading, setCostLoading] = useState(true);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);

  const fetchCostEstimate = useCallback(async (showLoader = false) => {
    if (showLoader) setCostLoading(true);
    try {
      const costRes = await productionApi.getJobCardCostEstimate(jobCardId);
      if (costRes.data.success) setCostData(costRes.data.data);
    } catch (err) {
      handleApiError(err, 'Failed to load job cost estimate');
    } finally {
      if (showLoader) setCostLoading(false);
    }
  }, [jobCardId]);

  const fetchJobDetails = useCallback(async () => {
    setLoading(true);
    try {
      const jobRes = await productionApi.getJobCardById(jobCardId);
      if (jobRes.data.success) setJob(jobRes.data.data);
      await fetchCostEstimate(true);
    } catch (err) {
      handleApiError(err, 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [fetchCostEstimate, jobCardId]);

  useEffect(() => {
    if (jobCardId) {
      fetchJobDetails();
    }
  }, [fetchJobDetails, jobCardId]);

  useEffect(() => {
    if (!jobCardId || activeTab !== 'cost_tracking') return;
    fetchCostEstimate(true);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchCostEstimate(false);
    };

    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') fetchCostEstimate(false);
    }, 30000);

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [activeTab, fetchCostEstimate, jobCardId]);

  useEffect(() => {
    if (!jobCardId || activeTab !== 'inventory_reconciliation') return;
    let mounted = true;
    const loadReconciliation = async () => {
      setReconciliationLoading(true);
      try {
        const res = await productionApi.getJobCardInventoryReconciliation(jobCardId);
        if (mounted && res.data?.success) {
          setReconciliationData(res.data);
        }
      } catch (err) {
        handleApiError(err, 'Failed to load inventory reconciliation');
      } finally {
        if (mounted) setReconciliationLoading(false);
      }
    };
    loadReconciliation();
    return () => {
      mounted = false;
    };
  }, [activeTab, jobCardId]);

  if (loading) return (
    <div className="h-full flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
        <span className="text-indigo-600 font-medium">Loading Job Data...</span>
      </div>
    </div>
  );

  if (!job) return (
    <div className="h-full flex items-center justify-center text-red-500">
      <div className="text-center">
        <AlertCircle size={48} className="mx-auto mb-3 text-red-400" />
        <h3 className="text-lg font-bold">Job Not Found</h3>
        <p className="text-sm text-slate-500 mt-1">The requested job card could not be found.</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg">Go Back</button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-8 py-5 flex justify-between items-start shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-slate-400 hover:text-indigo-600 transition-colors p-1 rounded-full hover:bg-indigo-50"
          >
            <ArrowRight className="rotate-180" size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{job.job_card_no}</h1>
            <p className="text-sm text-slate-500 mt-1">
              {job.design_code} • Qty: {job.quantity} • Order ID: {job.order_id || 'N/A'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onDuplicate(job)}
            className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            Duplicate Job Card
          </button>
          <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-xs font-bold shadow-sm">
            {job.status}
          </span>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 px-8">
        <div className="flex gap-6">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'cost_tracking', label: 'Cost Tracking' },
            { id: 'inventory_reconciliation', label: 'Inventory Reconciliation' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 max-w-5xl mx-auto w-full space-y-6">
        {activeTab === 'overview' ? (
          <>
            {['pre_casting', 'casting', 'post_casting'].map(key => {
              const stages = job.stages?.[key] || [];
              if (stages.length === 0) return null;
              const config = STAGE_CONFIG[key.toUpperCase()];
              return (
                <div key={key} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className={`px-6 py-4 border-b font-bold text-sm uppercase flex justify-between items-center ${key === 'pre_casting' ? 'bg-orange-50 text-orange-700' :
                      key === 'casting' ? 'bg-purple-50 text-purple-700' :
                        'bg-blue-50 text-blue-700'
                    }`}>
                    <span>{config.label}</span>
                    <span className="text-xs opacity-70 font-normal">{config.desc}</span>
                  </div>
                  <div className="p-6 space-y-3">
                    {stages.map(stage => <TimelineStage key={stage.stage_id} stage={stage} />)}
                  </div>
                </div>
              )
            })}

            {(!job.stages || Object.keys(job.stages).length === 0) && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
                <AlertCircle size={48} className="mx-auto mb-3 text-slate-300" />
                <h3 className="text-slate-500">No stages configured for this job card</h3>
              </div>
            )}
            <JobCardStageManager jobCardId={jobCardId} stages={job.stages} onRefresh={fetchJobDetails} />
          </>
        ) : activeTab === 'cost_tracking' ? (
          <CostBreakdownPanel costData={costData} loading={costLoading} jobStages={job.stages} />
        ) : (
          <InventoryReconciliationPanel data={reconciliationData} loading={reconciliationLoading} />
        )}
      </div>
    </div>
  );
};

// ==========================================
// MAIN APP COMPONENT
// ==========================================

export default function JobCardCreation() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState('JOB_CARDS');
  const [viewMode, setViewMode] = useState('LIST');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [createSeed, setCreateSeed] = useState(null);
  const [orderFilter, setOrderFilter] = useState('');
  const [creationResult, setCreationResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Pre-fill from Sales Order URL params (e.g. ?order_id=SO-2025-001&design_id=5&qty=2&customer_id=3)
    const params = new URLSearchParams(window.location.search);
    const prefillOrderId = params.get("order_id");
    if (prefillOrderId) {
      const prefillDesignId = parseInt(params.get("design_id")) || null;
      const prefillQty = parseInt(params.get("qty")) || '';
      const prefillCustomerId = parseInt(params.get("customer_id")) || '';
      const prefillSalesOrderId = params.get("sales_order_id") || prefillOrderId;
      const rawPrefillExpectedDeliveryDate = params.get("expected_delivery_date") || '';
      const prefillExpectedDeliveryDate = rawPrefillExpectedDeliveryDate
        ? rawPrefillExpectedDeliveryDate.split('T')[0].split(' ')[0]
        : '';
      const prefillRemarks = params.get("remarks") || '';
      setCreateSeed({
        order_id: prefillOrderId,
        design_id: prefillDesignId,
        quantity: prefillQty,
        customer_id: prefillCustomerId,
        expected_delivery_date: prefillExpectedDeliveryDate,
        remarks: prefillRemarks,
        fromSalesOrder: true,
        sales_order_id: prefillSalesOrderId,
      });
      setActiveScreen('JOB_CARDS');
      setViewMode('CREATE');
    }
  }, []);

  const handleNavigation = (path, screen) => {
    navigate(path);
    setActiveScreen(screen);
    setViewMode('LIST');
  };

  const handleCreateSuccess = (result) => {
    setCreationResult(result);
    setOrderFilter(result.orderId || '');
    setCreateSeed(null);
    setViewMode('LIST');
  };

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      <style>{`
        @media (max-width: ${BREAKPOINTS.mobile}) {
          .jc-sidebar {
            position: fixed;
            inset: 0 auto 0 0;
            width: 250px !important;
            transform: translateX(-100%);
            transition: transform 0.25s ease;
            z-index: 60;
          }
          .jc-sidebar.open {
            transform: translateX(0);
          }
          .jc-content-mobile {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
        }
      `}</style>
      {/* Sidebar */}
      <aside className={`jc-sidebar w-64 bg-white text-slate-900 flex flex-col border-r border-slate-200 shadow-sm z-20 ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-sm">
              <Gem className="text-white" size={24} />
            </div>
            <div>
              <span className="text-xl font-black text-slate-900 tracking-tighter uppercase italic">
                ZILVERRA<span className="text-blue-600">JEWELS</span>
              </span>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <div className="text-xs font-bold text-slate-500 uppercase px-3 mb-2 mt-4 tracking-wider">
            Core Modules
          </div>

          <button
            onClick={() => { handleNavigation("/production/master-design", 'DESIGN_MASTER'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${activeScreen === 'DESIGN_MASTER'
                ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                : 'text-slate-700 hover:bg-slate-100'
              }`}
          >
            <Settings size={20} />
            <span className="font-medium">Design Master</span>
          </button>

          <button
            onClick={() => { handleNavigation("/production/job-card", 'JOB_CARDS'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${activeScreen === 'JOB_CARDS'
                ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                : 'text-slate-700 hover:bg-slate-100'
              }`}
          >
            <Package size={20} />
            <span className="font-medium">Job Cards</span>
          </button>
        </nav>

        <div className="p-4 m-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold text-white">
              AD
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">Admin User</div>
              <div className="text-xs text-slate-500">Production Mgr</div>
            </div>
          </div>
        </div>
      </aside>
      {sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/40 z-50 md:hidden"
          aria-label="Close sidebar"
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50/50">
        {/* Header - Hide when in overview mode */}
        {viewMode !== 'OVERVIEW' && (
          <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 jc-content-mobile shadow-sm z-10">
            <div className="flex items-center gap-4 text-slate-500 text-sm">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1 rounded-md hover:bg-slate-100">
                <Menu size={18} />
              </button>
              <span onClick={() => navigate("/production")} className="hover:text-indigo-600 cursor-pointer transition-colors">
                Home
              </span>
              <span className="text-slate-300">/</span>
              <span className="font-medium text-slate-800">
                {activeScreen === 'DESIGN_MASTER' ? 'Design Management' : 'Production Jobs'}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold border border-emerald-100 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                System Online
              </span>
            </div>
          </header>
        )}

        {/* Content Area */}
        <div className={`flex-1 overflow-hidden relative ${viewMode === 'OVERVIEW' ? 'p-0' : 'p-6'}`}>
          {creationResult && viewMode === 'LIST' && activeScreen === 'JOB_CARDS' && (
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {creationResult.type === 'batch'
                ? `${creationResult.createdCount} job cards created for order ${creationResult.orderId}: ${creationResult.firstJobCardNo} to ${creationResult.lastJobCardNo}`
                : `Job card ${creationResult.firstJobCardNo} created for order ${creationResult.orderId}`}
              {creationResult.orderId && (
                <button
                  onClick={() => setOrderFilter(creationResult.orderId)}
                  className="ml-3 font-semibold text-emerald-800 underline"
                >
                  View All
                </button>
              )}
            </div>
          )}
          {activeScreen === 'DESIGN_MASTER' && viewMode === 'LIST' && <DesignMasterScreen />}

          {activeScreen === 'JOB_CARDS' && viewMode === 'LIST' && (
            <JobCardList
              onCreate={() => {
                setCreateSeed(null);
                setCreationResult(null);
                setViewMode('CREATE');
              }}
              onView={(id) => {
                setSelectedJobId(id);
                setViewMode('OVERVIEW');
              }}
              orderFilter={orderFilter}
              onClearOrderFilter={() => setOrderFilter('')}
            />
          )}

          {activeScreen === 'JOB_CARDS' && viewMode === 'CREATE' && (
            <JobCardCreate
              onCancel={() => setViewMode('LIST')}
              onSuccess={handleCreateSuccess}
              initialData={createSeed || {}}
            />
          )}

          {activeScreen === 'JOB_CARDS' && viewMode === 'OVERVIEW' && (
            <JobCardOverview
              jobCardId={selectedJobId}
              onBack={() => setViewMode('LIST')}
              onDuplicate={(job) => {
                setCreateSeed({
                  mode: 'single',
                  design_id: job.design_id,
                  order_id: job.order_id,
                  quantity: job.quantity,
                  expected_delivery_date: '',
                  customer_id: job.customer_id || '',
                  remarks: job.remarks || ''
                });
                setViewMode('CREATE');
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
}
